// Pestaña «Arbolado»: % de dosel arbóreo por colonia (Meta/WRI 1 m) + rankings +
// demostración de conteo árbol por árbol (DeepForest) en imagen de dron abierta.

import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import type { FeatureCollection } from 'geojson';
import { ScanSearch, Search, TreeDeciduous, Trees, AlertTriangle } from 'lucide-react';

import { Card, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import {
  DOSEL_LEYENDA, assetUrl, colorDosel, entero, loadColonias, mesAnio, num,
  type ColoniaProps, type Collection, type Resumen,
} from './data';
import { CARTO_KEY, reducedMotion, useTerritoryMap } from './useTerritoryMap';
import { Stat } from './Stat';
import { BaseSwitch } from './BaseSwitch';

const MIN_HA_RANKING = 20;

export function ArboladoView({ resumen }: { resumen: Resumen }) {
  const [data, setData] = useState<Collection<ColoniaProps> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const { map, setNode, base, setBase } = useTerritoryMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    loadColonias().then(setData).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el arbolado.'));
  }, []);

  useEffect(() => {
    if (!map || !data) return;
    layerRef.current?.remove();
    const gj = L.geoJSON(data as unknown as FeatureCollection, {
      style: (f) => {
        const p = f?.properties as ColoniaProps;
        const activo = p.colonia === sel;
        return {
          color: activo ? '#7C3AED' : '#475569', weight: activo ? 2.5 : 0.4, opacity: activo ? 1 : 0.6,
          fillColor: colorDosel(p.dosel_arboreo_pct), fillOpacity: 0.72,
        };
      },
      onEachFeature: (f, layer) => {
        const p = f.properties as ColoniaProps;
        layer.bindTooltip(`${p.colonia} · ${num(p.dosel_arboreo_pct)} % dosel`, { sticky: true, direction: 'top' });
        layer.on('click', () => setSel(p.colonia));
      },
    }).addTo(map);
    layerRef.current = gj;
  }, [map, data, sel]);

  useEffect(() => {
    if (!map || !layerRef.current) return;
    if (!sel) {
      const b = layerRef.current.getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [16, 16], animate: !reducedMotion() });
      return;
    }
    let target: L.LatLngBounds | null = null;
    layerRef.current.eachLayer((l) => {
      const p = (l as L.Polygon).feature?.properties as ColoniaProps | undefined;
      if (p?.colonia === sel) target = (l as L.Polygon).getBounds();
    });
    if (target) map.flyToBounds(target, { padding: [40, 40], maxZoom: 16, animate: !reducedMotion(), duration: 0.6 });
  }, [map, sel, data]);

  const colonias = useMemo(() => data?.features.map((f) => f.properties) ?? [], [data]);
  const grandes = useMemo(() => colonias.filter((c) => c.area_ha >= MIN_HA_RANKING), [colonias]);
  const masArboladas = useMemo(() => [...grandes].sort((a, b) => b.dosel_arboreo_pct - a.dosel_arboreo_pct).slice(0, 8), [grandes]);
  const menosArboladas = useMemo(() => [...grandes].sort((a, b) => a.dosel_arboreo_pct - b.dosel_arboreo_pct).slice(0, 8), [grandes]);
  const bajoUno = useMemo(() => grandes.filter((c) => c.dosel_arboreo_pct < 1).length, [grandes]);
  const resultados = useMemo(() => {
    const q = busca.trim().toLocaleLowerCase('es');
    if (q.length < 2) return [];
    return colonias.filter((c) => c.colonia.toLocaleLowerCase('es').includes(q)).slice(0, 8);
  }, [busca, colonias]);
  const elegida = colonias.find((c) => c.colonia === sel) ?? null;

  const d = resumen.dosel;
  const fechaImg = d.fechas_imagen.length ? mesAnio([...d.fechas_imagen].sort()[0]) : '—';

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={<Trees size={18} />} tone="violet" label="Dosel arbóreo (≥3 m)" value={`${num(d.aoi.dosel_arboreo_pct)} %`} sub="Zona metropolitana analizada" />
        <Stat icon={<TreeDeciduous size={18} />} tone="indigo" label="Colonias analizadas" value={entero(d.n_colonias)} sub="Polígonos de OpenStreetMap" />
        <Stat icon={<AlertTriangle size={18} />} tone="magenta" label="Colonias con < 1 % de dosel" value={entero(bajoUno)} sub={`De ${entero(grandes.length)} con ≥ ${MIN_HA_RANKING} ha`} />
        <Stat icon={<ScanSearch size={18} />} tone="blue" label="Fecha de la imagen" value={fechaImg} sub="Mapa de altura de dosel 1 m" />
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
        <Card className="overflow-hidden" bodyClassName="!p-0">
          <div className="gt-map relative">
            <div ref={setNode} className="h-[380px] w-full sm:h-[520px] lg:h-[600px]" role="region" aria-label="Mapa de dosel arbóreo por colonia" />
            <BaseSwitch value={base} onChange={setBase} />
            {!CARTO_KEY && base === 'mapa' && (
              <p className="gt-nobase rounded-md bg-card/90 px-2 py-0.5 text-[10px] text-muted-foreground">Mapa base no configurado · se muestran solo los datos</p>
            )}
            <div className="pointer-events-none absolute bottom-6 left-3 z-[500] rounded-xl border border-border bg-card/90 px-3 py-2 text-[11px] text-foreground shadow-sm backdrop-blur">
              <p className="mb-1 font-medium">% de dosel ≥ 3 m</p>
              <div className="flex items-center gap-0.5">
                {DOSEL_LEYENDA.map((x) => (
                  <span key={x} className="flex flex-col items-center gap-0.5">
                    <span aria-hidden className="h-2.5 w-6 rounded-sm" style={{ background: colorDosel(x) }} />
                    <span className="tabular-nums text-muted-foreground">{x === 40 ? '40+' : x}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card bodyClassName="!pt-4 flex flex-col gap-3">
          {error && <ErrorNote>{error}</ErrorNote>}
          {!data && !error && <Loading label="Cargando colonias…" />}
          {data && (
            <>
              <label className="relative block">
                <span className="sr-only">Buscar colonia</span>
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar colonia…"
                  className="h-10 w-full rounded-xl border border-input bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground" />
              </label>
              {resultados.length > 0 && (
                <div className="flex flex-col gap-0.5 rounded-xl border border-border p-1">
                  {resultados.map((c) => (
                    <button key={c.colonia} type="button" onClick={() => { setSel(c.colonia); setBusca(''); }}
                      className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted">
                      <span className="truncate text-foreground">{c.colonia}</span>
                      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{num(c.dosel_arboreo_pct)} %</span>
                    </button>
                  ))}
                </div>
              )}

              {elegida && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 text-sm font-semibold text-foreground">{elegida.colonia}</p>
                    <button type="button" onClick={() => setSel(null)} className="text-xs text-muted-foreground hover:text-foreground">Quitar</button>
                  </div>
                  <dl className="mt-2 grid grid-cols-3 gap-2 text-center">
                    <div><dt className="text-[11px] text-muted-foreground">Dosel</dt><dd className="font-semibold tabular-nums text-foreground">{num(elegida.dosel_arboreo_pct)} %</dd></div>
                    <div><dt className="text-[11px] text-muted-foreground">Arbustos</dt><dd className="font-semibold tabular-nums text-foreground">{num(elegida.arbustivo_pct)} %</dd></div>
                    <div><dt className="text-[11px] text-muted-foreground">Superficie</dt><dd className="font-semibold tabular-nums text-foreground">{num(elegida.area_ha, 0)} ha</dd></div>
                  </dl>
                  <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                    Árboles estimados: ~{entero(elegida.arboles_estimados)}. Es un estimador conservador, porque las copas juntas cuentan como una; la cifra confiable es el % de dosel.
                  </p>
                </div>
              )}

              <Ranking titulo="Más arboladas" items={masArboladas} onPick={setSel} />
              <Ranking titulo="Menos arboladas · candidatas a reforestar" items={menosArboladas} onPick={setSel} />
            </>
          )}
        </Card>
      </div>

      <ConteoDemo resumen={resumen} />
    </div>
  );
}

function Ranking({ titulo, items, onPick }: { titulo: string; items: ColoniaProps[]; onPick: (c: string) => void }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</p>
      <ol className="flex flex-col">
        {items.map((c, i) => (
          <li key={c.colonia}>
            <button type="button" onClick={() => onPick(c.colonia)}
              className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left text-sm hover:bg-muted">
              <span className="w-4 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{i + 1}</span>
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: colorDosel(c.dosel_arboreo_pct) }} />
              <span className="min-w-0 flex-1 truncate text-foreground">{c.colonia}</span>
              <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{num(c.dosel_arboreo_pct)} %</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ConteoDemo({ resumen }: { resumen: Resumen }) {
  const ventanas = resumen.conteo.ventanas;
  const [i, setI] = useState(0);
  const w = ventanas[i];
  if (!w) return null;
  return (
    <Card title="Conteo árbol por árbol">
      <span className="mb-3 inline-flex rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
        Demostración de técnica · no es San Luis Potosí
      </span>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,420px)_1fr]">
        <figure className="overflow-hidden rounded-xl border border-border bg-muted">
          <img src={assetUrl(`img/${w.img}`)} alt={`Detecciones de árboles de DeepForest: ${w.fuente}, ${w.ventana}`} className="aspect-square w-full object-cover" loading="lazy" />
        </figure>
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Para contar árboles uno por uno hace falta imagen de 10 a 30 cm. <span className="text-foreground">No existe imagen abierta así del municipio de San Luis Potosí</span>, así que mostramos
            la técnica (DeepForest, licencia MIT, en CPU) sobre vuelos de dron abiertos (CC-BY 4.0) de Naucalpan y Querétaro. Con las tomas del municipio, esto mismo corre sobre la capital.
          </p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Elegir ventana de muestra">
            {ventanas.map((x, k) => (
              <button key={`${x.fuente}-${x.ventana}`} type="button" aria-pressed={k === i} onClick={() => setI(k)}
                className={`inline-flex h-8 items-center rounded-full border px-3 text-[12.5px] font-medium transition-colors ${
                  k === i ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
                {x.fuente === 'naucalli' ? 'Naucalpan' : 'Querétaro'} · {x.ventana.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
          <dl className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl border border-border p-2"><dt className="text-[11px] text-muted-foreground">Árboles detectados</dt><dd className="text-lg font-semibold tabular-nums text-foreground">{entero(w.arboles)}</dd></div>
            <div className="rounded-xl border border-border p-2"><dt className="text-[11px] text-muted-foreground">Árboles por ha</dt><dd className="text-lg font-semibold tabular-nums text-foreground">{entero(w.arboles_por_ha)}</dd></div>
            <div className="rounded-xl border border-border p-2"><dt className="text-[11px] text-muted-foreground">CPU por ha</dt><dd className="text-lg font-semibold tabular-nums text-foreground">{Math.round(w.t_inferencia_cpu_s)} s</dd></div>
          </dl>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{w.titulo}. Sin validación de campo: en copas continuas el modelo cuenta de menos. Para medir su precisión hacen falta árboles del inventario municipal.</p>
        </div>
      </div>
    </Card>
  );
}
